import { type ReactNode, useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ArrowLeftIcon from "lucide-react/dist/esm/icons/arrow-left";
import Link2Icon from "lucide-react/dist/esm/icons/link-2";
import Layers3Icon from "lucide-react/dist/esm/icons/layers-3";
import TagsIcon from "lucide-react/dist/esm/icons/tags";
import { Badge } from "@quake/shared-ui/components/ui/badge";
import { Button } from "@quake/shared-ui/components/ui/button";
import { trpc } from "~/utils/trpc";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import { SkillInspector } from "./-components/skill-inspector";
import { SkillLinksSection } from "./-components/skill-links-section";

export const Route = createFileRoute("/_dashboard/$datasetSlug/data/skills/$skillId")({
  component: SkillDetailsRouteComponent,
});

function SkillDetailsRouteComponent() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { datasetSlug, skillId } = Route.useParams();
  const detailQuery = useQuery(trpc.skills.getSkillDetail.queryOptions({ id: skillId }));
  const detail = detailQuery.data;

  const updateSkill = useMutation(trpc.skills.updateSkill.mutationOptions({ onSuccess: invalidateWorkspace(qc) }));
  const deleteSkill = useMutation(trpc.skills.deleteSkill.mutationOptions({ onSuccess: invalidateWorkspace(qc) }));
  const createAlias = useMutation(trpc.skills.createAlias.mutationOptions({ onSuccess: invalidateWorkspace(qc) }));
  const updateAlias = useMutation(trpc.skills.updateAlias.mutationOptions({ onSuccess: invalidateWorkspace(qc) }));
  const deleteAlias = useMutation(trpc.skills.deleteAlias.mutationOptions({ onSuccess: invalidateWorkspace(qc) }));
  const setPresetSkillMembership = useMutation(
    trpc.skills.setPresetSkillMembership.mutationOptions({ onSuccess: invalidateWorkspace(qc) }),
  );
  const createLink = useMutation(trpc.skills.createLink.mutationOptions({ onSuccess: invalidateWorkspace(qc) }));
  const updateLink = useMutation(trpc.skills.updateLink.mutationOptions({ onSuccess: invalidateWorkspace(qc) }));
  const deleteLink = useMutation(trpc.skills.deleteLink.mutationOptions({ onSuccess: invalidateWorkspace(qc) }));

  const skill = detail?.skill ?? null;
  const aliases = detail?.aliases ?? [];
  const presetsCount = detail?.presetMemberships.filter((preset) => preset.has_skill).length ?? 0;
  const linksCount = detail?.links.length ?? 0;

  const [skillDraft, setSkillDraft] = useState({
    id: "",
    name: "",
    description: "",
    skill_category_id: "",
    status: "active",
  });
  const [newAlias, setNewAlias] = useState("");

  useEffect(() => {
    if (!skill) return;
    setSkillDraft({
      id: skill.id,
      name: skill.name,
      description: skill.description ?? "",
      skill_category_id: skill.skill_category_id ?? "",
      status: skill.status,
    });
  }, [skill]);

  useBreadCrumbs([
    { title: "Skills", link: `/${datasetSlug}/data/skills` },
    { title: skill?.name ?? "Skill details", link: `/${datasetSlug}/data/skills/${skillId}` },
  ]);

  if (detailQuery.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading skill details…</div>;
  }

  if (detail === undefined) {
    return <div className="p-6 text-sm text-muted-foreground">Skills workspace unavailable.</div>;
  }

  if (!skill || !detail) {
    return (
      <div className="flex min-h-[calc(100vh-4.5rem)] items-center justify-center px-6">
        <div className="w-full max-w-xl rounded-4xl border border-black/5 bg-white/80 p-8 text-center shadow-[0_20px_70px_-50px_rgba(15,23,42,0.4)] dark:border-white/10 dark:bg-white/4">
          <p className="text-[0.68rem] uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Missing</p>
          <h1 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950 dark:text-slate-50">
            This skill no longer exists
          </h1>
          <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
            It may have been deleted or moved out of this dataset.
          </p>
          <Button asChild className="mt-6">
            <Link to="/$datasetSlug/data/skills" params={{ datasetSlug }}>
              Back to skills
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4.5rem)]">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 px-6 py-6">
        <div>
          <Link
            to="/$datasetSlug/data/skills"
            params={{ datasetSlug }}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeftIcon className="size-4" />
            Back to taxonomy
          </Link>
        </div>

        <header>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-3xl">
              <h1 className="text-2xl font-bold tracking-tight">{skill.name}</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Edit this skill’s details, aliases, skill sets, and links.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <StatChip icon={<TagsIcon className="size-4" />} label={skill.skill_category_label ?? "Unassigned"} />
              <StatChip
                icon={<Layers3Icon className="size-4" />}
                label={`${presetsCount} set${presetsCount === 1 ? "" : "s"}`}
              />
              <StatChip
                icon={<Link2Icon className="size-4" />}
                label={`${linksCount} link${linksCount === 1 ? "" : "s"}`}
              />
              <Badge variant="secondary" className="capitalize">
                {skill.status}
              </Badge>
            </div>
          </div>
        </header>

        <div className="space-y-5">
          <section className="space-y-5">
            <SkillInspector
              variant="page"
              skillCategories={detail.skillCategories}
              presetMemberships={detail.presetMemberships}
              selectedSkill={skill}
              aliases={aliases}
              skillDraft={skillDraft}
              setSkillDraft={setSkillDraft}
              newAlias={newAlias}
              setNewAlias={setNewAlias}
              onSaveSkill={(input) => updateSkill.mutate(input)}
              onDeleteSkill={async (id) => {
                await deleteSkill.mutateAsync({ id });
                await navigate({ to: "/$datasetSlug/data/skills", params: { datasetSlug } });
              }}
              onSaveAlias={(aliasId, next) => updateAlias.mutate({ id: aliasId, skill_id: skill.id, ...next })}
              onDeleteAlias={(id) => deleteAlias.mutate({ id })}
              onAddAlias={(aliasText) =>
                createAlias.mutate({
                  skill_id: skill.id,
                  alias_text: aliasText,
                })
              }
              onSavePresetMembership={(input) => setPresetSkillMembership.mutateAsync({ skill_id: skill.id, ...input })}
            />
          </section>

          <SkillLinksSection
            datasetSlug={datasetSlug}
            links={detail.links}
            skillLinkTypes={detail.skillLinkTypes}
            skill={skill}
            onCreateLink={createLink.mutateAsync}
            onUpdateLink={updateLink.mutateAsync}
            onDeleteLink={(id) => deleteLink.mutateAsync({ id })}
          />
        </div>
      </div>
    </div>
  );
}

function StatChip({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
      {icon}
      <span>{label}</span>
    </span>
  );
}

function invalidateWorkspace(qc: ReturnType<typeof useQueryClient>) {
  return async () => {
    await Promise.all([
      qc.invalidateQueries(trpc.skills.getSkillDetail.queryFilter()),
      qc.invalidateQueries(trpc.skills.listSkills.queryFilter()),
      qc.invalidateQueries(trpc.skills.getCatalogMeta.queryFilter()),
      qc.invalidateQueries(trpc.skills.searchSkillOptions.queryFilter()),
    ]);
  };
}
